import { getBusinessMessages, getEmbeddedSignupByPhone, getWhatsAppCallRecords } from "../../../api/login.service";
import { BusinessLead, LeadFormMessage } from "../../../api/leads.service";
import { LastCall } from "./businessSetup";
import { getInstagramMessageUsers } from "../../../api/instagram.service";

export type ChatChannel = "whatsapp" | "instagram" | "web";

export type ChatRow = {
  key: string;
  channel: ChatChannel;
  name: string;
  handle: string;
  preview: string;
  at: string;
  unread: number;
  customerNumber: string;
  customerId?: string;
  instagramId?: string;
  formFilled?: string;
  forms?: LeadFormMessage[];
};

export function channelThread(lead: ChatRow, channel: ChatChannel): ChatRow | null {
  if (channel === "instagram" && (lead.customerId || lead.instagramId)) return { ...lead, channel: "instagram" };
  if (channel === "whatsapp" && lead.customerNumber) return { ...lead, channel: "whatsapp" };
  return null;
}

export type ConnectedAccount = {
  phoneNumberId: string;
  businessPhone: string;
  instagramId: string;
};

export async function loadAccount(displayPhone: string | null): Promise<ConnectedAccount> {
  const businessPhone = displayPhone || sessionStorage.getItem("display_phone_number") || "";
  let phoneNumberId = sessionStorage.getItem("phoneNumberId") || "";
  let instagramId = "";

  if (businessPhone) {
    try {
      const data = await getEmbeddedSignupByPhone(businessPhone);
      const record = data?.records?.[0];
      if (record?.phoneNumberId) {
        phoneNumberId = String(record.phoneNumberId);
        sessionStorage.setItem("phoneNumberId", phoneNumberId);
      }
      if (record?.instagramUserId) {
        instagramId = String(record.instagramUserId);
      }
    } catch {
      // The session values above are enough to try the message APIs.
    }
  }

  return { phoneNumberId, businessPhone, instagramId };
}

/** The name the person gave. A numeric Instagram id is not a name. */
export function personName(raw: string): string {
  const name = String(raw || "").replace(/^@/, "").trim();
  if (!name || /^\d+$/.test(name) || /^lead$/i.test(name)) return "";
  return name;
}

export function leadsToChats(leads: BusinessLead[]): ChatRow[] {
  return leads.map((lead) => {
    const instagram = (lead.instagramId || "").replace(/^@/, "");
    const channel: ChatChannel =
      lead.connection === "whatsapp" || lead.connection === "instagram" || lead.connection === "web"
        ? lead.connection
        : lead.whatsappNumber
          ? "whatsapp"
          : instagram
            ? "instagram"
            : "web";
    const handle = instagram && !/^\d+$/.test(instagram) ? `@${instagram}` : lead.whatsappNumber;
    return {
      key: `lead:${lead.conversationId}`,
      channel,
      name: personName(lead.name) || "Lead",
      handle,
      preview: "",
      at: lead.updatedAt,
      unread: 0,
      customerNumber: lead.whatsappNumber,
      customerId: lead.instagramCustomerId && lead.instagramCustomerId.replace(/^@/, "").toLowerCase() !== instagram.toLowerCase()
        ? lead.instagramCustomerId
        : undefined,
      instagramId: instagram,
      formFilled: lead.formFilled === "yes" ? "yes" : "no",
      forms: lead.forms || [],
    };
  });
}

export async function loadWhatsAppChats(phoneNumberId: string): Promise<ChatRow[]> {
  if (!phoneNumberId) return [];
  const result = await getBusinessMessages(phoneNumberId);
  const rawMessages = (result?.messages || []) as unknown as Array<Record<string, unknown>>;
  const messages = rawMessages;
  const byCustomer = new Map<string, ChatRow>();

  messages.forEach((msg) => {
    const number = String(msg.customerNumber || "");
    if (!number) return;
    const at = String(msg.updatedAt || msg.timestamp || "");
    const existing = byCustomer.get(number);
    if (existing && new Date(existing.at).getTime() > new Date(at).getTime()) return;
    byCustomer.set(number, {
      key: `wa:${number}`,
      channel: "whatsapp",
      name: String(msg.customerName || number),
      handle: number,
      preview: String(msg.message || "No message yet"),
      at,
      unread: Number(msg.unreadCount || 0),
      customerNumber: number,
    });
  });

  return Array.from(byCustomer.values()).sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()
  );
}

export async function loadInstagramChats(instagramId: string): Promise<ChatRow[]> {
  const users = await getInstagramMessageUsers(instagramId || undefined);
  return users
    .map((user) => {
      const username = (user.username || "").replace(/^@/, "");
      const handle = username ? `@${username}` : user.customerId;
      return {
        key: `ig:${user.customerId}`,
        channel: "instagram" as const,
        name: user.name || user.customerName || handle,
        handle,
        preview: user.lastMessage || "No message yet",
        at: user.updatedAt || user.timestamp || "",
        unread: 0,
        customerNumber: handle,
        customerId: user.customerId,
      };
    })
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

export type CallTouch = {
  row: ChatRow;
  lastCall: LastCall;
};

function digits(value: string): string {
  return value.replace(/\D/g, "");
}

function callNumber(call: Record<string, unknown>): string {
  return String(
    call.customerNumber || call.customer_number || call.phoneNumber || call.phone || call.from || call.to || call.callerId || call.caller_id || ""
  );
}

function callDirection(call: Record<string, unknown>): string {
  const raw = String(call.call_direction || call.direction || call.type || call.call_type || "call").toLowerCase();
  if (raw.includes("in")) return "incoming";
  if (raw.includes("out")) return "outgoing";
  return "call";
}

export async function loadCallTouches(phoneNumberId: string): Promise<CallTouch[]> {
  if (!phoneNumberId) return [];
  const result = await getWhatsAppCallRecords(phoneNumberId);
  const records = (result.records || result.calls || result.data || []) as Array<Record<string, unknown>>;
  const byNumber = new Map<string, CallTouch>();

  records.forEach((call) => {
    const number = callNumber(call).trim();
    const id = digits(number);
    if (!id) return;
    const at = String(call.timestamp || call.call_time || call.created_at || call.createdAt || "");
    const existing = byNumber.get(id);
    if (existing && new Date(existing.row.at).getTime() > new Date(at).getTime()) return;
    const duration = call.call_duration_seconds ?? call.duration ?? call.call_duration ?? call.duration_seconds;
    const direction = callDirection(call);
    byNumber.set(id, {
      row: {
        key: `wa:${id}`,
        channel: "whatsapp",
        name: String(call.caller_name || call.customerName || call.customer_name || call.name || number),
        handle: number,
        preview: `Phone call · ${direction}`,
        at,
        unread: 0,
        customerNumber: number,
      },
      lastCall: {
        at,
        direction,
        durationSeconds: typeof duration === "number" ? duration : null,
      },
    });
  });

  return Array.from(byNumber.values());
}

export function mergeCallRows(chats: ChatRow[], touches: CallTouch[]): { rows: ChatRow[]; calls: Array<{ key: string; lastCall: LastCall }> } {
  const rows = chats.map((chat) => ({ ...chat }));
  const calls: Array<{ key: string; lastCall: LastCall }> = [];

  touches.forEach((touch) => {
    const id = digits(touch.row.customerNumber);
    const existing = rows.find((chat) => chat.channel === "whatsapp" && digits(chat.customerNumber) === id);
    const key = existing?.key || touch.row.key;
    if (!existing) rows.push({ ...touch.row, key });
    calls.push({ key, lastCall: touch.lastCall });
  });

  rows.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  return { rows, calls };
}

export function ago(iso: string): string {
  const time = new Date(iso).getTime();
  if (!time) return "";
  const minutes = Math.round((Date.now() - time) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function initials(name: string): string {
  const parts = name.replace(/[^a-zA-Z0-9 ]/g, " ").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.slice(0, 2).map((part) => part[0].toUpperCase()).join("");
}
